import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ClipboardCheck, Plus, Trash2 } from '../../components/icons';
import { fetchTasks, createTask, updateTask, deleteTask, type Task } from '../../lib/progress';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const STATUS_CYCLE: Task['status'][] = ['todo', 'in_progress', 'done'];
const STATUS_LABELS: Record<Task['status'], string> = { todo: 'To Do', in_progress: 'In Progress', done: 'Done' };
const STATUS_STYLES: Record<Task['status'], string> = {
  todo: 'bg-cream-100 text-chocolate-500',
  in_progress: 'bg-amber-100 text-amber-700',
  done: 'bg-sage-100 text-sage-700',
};
const PRIORITY_STYLES: Record<Task['priority'], string> = {
  low: 'text-chocolate-300',
  medium: 'text-amber-500',
  high: 'text-coral-500',
};

type Filter = 'all' | Task['status'];

export function TasksView() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium' as Task['priority'], due_date: '' });

  useEffect(() => {
    if (!user) return;
    fetchTasks(user.id).then(setTasks).finally(() => setIsLoading(false));
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    const created = await createTask(user.id, { ...form, status: 'todo' });
    if (created) setTasks(prev => [created, ...prev]);
    setShowForm(false);
    setForm({ title: '', description: '', priority: 'medium', due_date: '' });
  };

  const handleCycleStatus = async (task: Task) => {
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(task.status) + 1) % STATUS_CYCLE.length];
    await updateTask(task.id, { status: next });
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: next } : t));
  };

  const handleDelete = async (id: string) => {
    await deleteTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  const filtered = filter === 'all' ? tasks : tasks.filter(t => t.status === filter);

  if (isLoading) return <LoadingSpinner message="Loading tasks..." />;

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Tasks</h1>
            <p className="text-sm text-chocolate-500">{tasks.filter(t => t.status !== 'done').length} open</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Task
        </button>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">New Task</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Title *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="What needs to be done?" required />
            </div>
            <div>
              <label className="freud-label">Description</label>
              <textarea className="freud-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Priority</label>
                <select className="freud-input" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value as Task['priority'] }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="freud-label">Due Date</label>
                <input type="date" className="freud-input" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">Create Task</button>
              <button type="button" onClick={() => setShowForm(false)} className="freud-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-cream-100 p-1 rounded-xl w-fit">
        {(['all', 'todo', 'in_progress', 'done'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
          >
            {f === 'all' ? 'All' : STATUS_LABELS[f]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">{filter === 'all' ? 'No tasks yet.' : `No ${STATUS_LABELS[filter as Task['status']]} tasks.`}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(task => (
            <div key={task.id} className={`freud-card-elevated p-4 flex items-start gap-3 ${task.status === 'done' ? 'opacity-60' : ''}`}>
              <button onClick={() => handleCycleStatus(task)} className="mt-0.5 flex-shrink-0" title="Click to advance status">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  task.status === 'done' ? 'bg-sage-500 border-sage-500' : task.status === 'in_progress' ? 'border-amber-400 bg-amber-50' : 'border-chocolate-300'
                }`}>
                  {task.status === 'done' && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  {task.status === 'in_progress' && <div className="w-2 h-2 rounded-sm bg-amber-400" />}
                </div>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[task.status]}`}>{STATUS_LABELS[task.status]}</span>
                  <svg className={`w-3 h-3 ${PRIORITY_STYLES[task.priority]}`} fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h18v2H3V3zm0 4h12v2H3V7zm0 4h18v2H3v-2zm0 4h12v2H3v-2z"/></svg>
                  <span className={`text-[11px] capitalize ${PRIORITY_STYLES[task.priority]}`}>{task.priority}</span>
                  {task.due_date && <span className="text-[11px] text-chocolate-400">Due {new Date(task.due_date).toLocaleDateString()}</span>}
                </div>
                <p className={`text-sm font-medium text-chocolate-900 ${task.status === 'done' ? 'line-through' : ''}`}>{task.title}</p>
                {task.description && <p className="text-xs text-chocolate-500 mt-0.5">{task.description}</p>}
              </div>
              <button onClick={() => handleDelete(task.id)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
